import { Alert, Divider, Paper, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import type { CommissionRulePreview } from './types';

export function CalculationPreview({ preview }: { readonly preview: CommissionRulePreview }) {
  const { t } = useTranslation();
  const calculation = preview.calculation;
  return (
    <Paper component="section" variant="outlined" sx={{ p: 2 }}>
      <Typography component="h2" variant="h5">
        {t('commissionRules.preview.title')}
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 2 }} variant="body2">
        {t('commissionRules.preview.serverValues')}
      </Typography>
      <Stack divider={<Divider flexItem />} spacing={1}>
        <PreviewLine
          label={t('commissionRules.fields.actualPlayAmount')}
          value={`${calculation.actualPlayAmount} ${calculation.currencyCode}`}
        />
        <PreviewLine
          label={t('commissionRules.fields.agentCommissionUnrounded')}
          value={calculation.agentCommissionUnrounded}
        />
        <PreviewLine
          label={t('commissionRules.fields.agentCommission')}
          value={`${calculation.agentCommission} ${calculation.currencyCode}`}
        />
        <Typography variant="caption">
          {t('commissionRules.preview.agentFormula', {
            amount: calculation.actualPlayAmount,
            rate: preview.configuration.agentRatePercent,
          })}
        </Typography>
        <PreviewLine
          label={t('commissionRules.fields.affiliateCommissionUnrounded')}
          value={calculation.affiliateCommissionUnrounded}
        />
        <PreviewLine
          label={t('commissionRules.fields.affiliateCommission')}
          value={`${calculation.affiliateCommission} ${calculation.currencyCode}`}
        />
        <Typography variant="caption">
          {t('commissionRules.preview.affiliateFormula', {
            amount: calculation.actualPlayAmount,
            rate: preview.configuration.affiliateRatePercent,
          })}
        </Typography>
        <PreviewLine
          label={t('commissionRules.fields.totalCommission')}
          value={`${calculation.totalCommission} ${calculation.currencyCode}`}
        />
      </Stack>
      <Alert severity="warning" sx={{ mt: 2 }}>
        {t('commissionRules.preview.impact', { count: preview.impactedPartnerCount })}
      </Alert>
      {preview.warnings.map((notice) => (
        <Alert key={notice.code} severity="warning" sx={{ mt: 1 }}>
          {safeNotice(notice.code, 'warning', t)}
        </Alert>
      ))}
      {preview.blockers.map((notice) => (
        <Alert key={notice.code} severity="error" sx={{ mt: 1 }}>
          {safeNotice(notice.code, 'blocker', t)}
        </Alert>
      ))}
      <Typography component="h3" sx={{ mt: 2 }} variant="h6">
        {t('commissionRules.preview.normalization')}
      </Typography>
      <Stack spacing={0.5} sx={{ mt: 1 }}>
        {Object.entries(preview.configuration).map(([key, value]) => (
          <PreviewLine
            key={key}
            label={t(`commissionRules.configuration.${key}`)}
            value={value === null ? t('common.notAvailable') : String(value)}
          />
        ))}
      </Stack>
    </Paper>
  );
}

function PreviewLine({ label, value }: { readonly label: string; readonly value: string }) {
  return (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      spacing={1}
      sx={{ justifyContent: 'space-between' }}
    >
      <Typography color="text.secondary" variant="body2">
        {label}
      </Typography>
      <Typography sx={{ fontFamily: 'monospace', overflowWrap: 'anywhere' }} variant="body2">
        {value}
      </Typography>
    </Stack>
  );
}

function safeNotice(code: string, kind: 'warning' | 'blocker', t: (key: string) => string): string {
  const known: Readonly<Record<string, string>> = {
    EFFECTIVE_PERIOD_OVERLAP: 'commissionRules.notices.overlap',
    PARTNER_IMPACT: 'commissionRules.notices.partnerImpact',
    GAME_UNAVAILABLE: 'commissionRules.notices.gameUnavailable',
    RATE_TOTAL_EXCEEDED: 'commissionRules.notices.rateTotalExceeded',
  };
  return t(known[code] ?? `commissionRules.notices.${kind}Fallback`);
}
